import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { CONTACT } from "@/lib/contact";

const links = [
  { to: "/", label: "Home" },
  { to: "/fleet", label: "Fleet" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/my-bookings", label: "My Bookings" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-dark py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img
            src={LOGO_URL}
            alt="PB-Lux Vehicle Rental"
            className="h-11 w-11 rounded-full border border-gold-500/30 group-hover:border-gold-400 transition-all"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-xl tracking-wide text-gold-200">PB-LUX</span>
            <span className="text-[10px] tracking-[0.3em] text-gold-500/80 font-sans-display uppercase">
              Vehicle Rental
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            className="hidden md:flex items-center gap-2 text-xs text-gold-300/80 hover:text-gold-200 transition-colors"
            data-testid="navbar-phone"
          >
            <Phone className="w-3.5 h-3.5" /> {CONTACT.phone}
          </a>
          <Link to="/fleet" className="hidden md:inline-flex btn-gold !py-2.5 !px-5 !text-[11px]" data-testid="navbar-book-cta">
            Book Now
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-gold-200"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden glass-dark border-t border-gold-500/15 mt-3 animate-fade-in" data-testid="navbar-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) => `nav-link text-base ${isActive ? "active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/fleet" className="btn-gold w-full !py-3" data-testid="mobile-book-cta">
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
